"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import MediaDisplay from "@/components/drops/view/item/content/media/MediaDisplay";
import { useUserArtSubmissions, useSubmissionDrops } from "@/hooks/useUserArtSubmissions";
import { useCompactMode } from "@/contexts/CompactModeContext";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { SingleWaveDropVote, SingleWaveDropVoteSize } from "../drop/SingleWaveDropVote";
import { SubmissionPosition } from "./SubmissionPosition";
import { ApiProfileMin } from "@/generated/models/ApiProfileMin";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import DropVoteProgressing from "@/components/drops/view/utils/DropVoteProgressing";
import { formatNumberWithCommas } from "@/helpers/Helpers";
interface ArtistActiveSubmissionContentProps {
  readonly user: ApiProfileMin;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly isApp?: boolean | undefined;
}
export const ArtistActiveSubmissionContent: React.FC<ArtistActiveSubmissionContentProps> = t0 => {
  const $ = _c(22);
  const {
    user,
    isOpen,
    onClose,
    isApp: t1
  } = t0;
  const isApp = t1 === undefined ? false : t1;
  const queryClient = useQueryClient();
  const {
    submissions,
    isLoading
  } = useUserArtSubmissions(isOpen ? user : undefined);
  const {
    submissionsWithDrops,
    isLoading: dropsLoading
  } = useSubmissionDrops(submissions);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const compact = useCompactMode();
  const isSmallScreen = useMediaQuery("(max-width: 1023px)");
  const formatDate = _temp;
  let t2;
  if ($[0] !== compact || $[1] !== isSmallScreen || $[2] !== onClose || $[3] !== pathname || $[4] !== router || $[5] !== searchParams) {
    t2 = dropId => {
      if (!searchParams) {
        return;
      }
      const params = new URLSearchParams(searchParams.toString());
      params.set("drop", dropId);
      router.push(`${pathname}?${params.toString()}`);
      if (compact && isSmallScreen && globalThis.window !== undefined) {
        globalThis.window.dispatchEvent(new CustomEvent("single-drop:close-chat"));
      }
      onClose();
    };
    $[0] = compact;
    $[1] = isSmallScreen;
    $[2] = onClose;
    $[3] = pathname;
    $[4] = router;
    $[5] = searchParams;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleDropClick = t2;
  let t3;
  if ($[7] !== queryClient) {
    t3 = () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DROP]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.PROFILE_DROPS]
      });
    };
    $[7] = queryClient;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleVoteSuccess = t3;
  if (!searchParams) {
    return null;
  }
  const t4 = `tw-relative tw-z-[100] tw-p-6 ${isApp ? "" : "tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 hover:tw-scrollbar-thumb-iron-300 tw-max-h-[calc(75vh-120px)] sm:tw-max-h-[calc(90vh-160px)]"}`;
  let t5;
  bb0: {
    if (isLoading || dropsLoading) {
      let t6;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <div className="tw-flex tw-items-center tw-justify-center tw-h-96"><div className="tw-flex tw-flex-col tw-items-center tw-gap-4"><div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b tw-border-solid tw-border-t-0 tw-border-x-0 tw-border-iron-400" /><span className="tw-text-iron-400 tw-text-sm tw-animate-fade-in-out">Loading submissions...</span></div></div>;
        $[9] = t6;
      } else {
        t6 = $[9];
      }
      t5 = t6;
      break bb0;
    }
    let t6;
    if ($[10] !== handleDropClick || $[11] !== handleVoteSuccess || $[12] !== submissionsWithDrops) {
      let t7;
      if ($[14] !== handleDropClick || $[15] !== handleVoteSuccess) {
        t7 = submission => <div key={submission.id} className="tw-flex tw-flex-col tw-h-full"><div className="tw-group tw-relative tw-cursor-pointer tw-flex tw-flex-col tw-flex-1 tw-bg-iron-950/50 tw-border tw-border-solid tw-border-iron-800 tw-rounded-lg tw-overflow-hidden desktop-hover:hover:tw-border-iron-700 tw-shadow-lg desktop-hover:hover:tw-shadow-xl tw-transition-all tw-duration-300 tw-ease-out tw-mb-3" onClick={() => handleDropClick(submission.id)}><div className="tw-aspect-square tw-relative tw-overflow-hidden tw-bg-iron-950/50"><div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center"><MediaDisplay media_url={submission.imageUrl} media_mime_type={submission.mediaMimeType} disableMediaInteraction={true} /></div><div className="tw-absolute tw-top-3 tw-right-3 tw-opacity-0 desktop-hover:group-hover:tw-opacity-100 tw-transition-opacity tw-duration-300"><div className="tw-w-8 tw-h-8 tw-bg-black/50 tw-backdrop-blur-sm tw-rounded-full tw-flex tw-items-center tw-justify-center"><FontAwesomeIcon icon={faEye} className="tw-w-3.5 tw-h-3.5 tw-text-white" /></div></div></div><div className="tw-p-3 tw-border-t tw-border-solid tw-border-x-0 tw-border-b-0 tw-border-iron-800 tw-bg-iron-950/50 tw-flex tw-flex-col tw-flex-1 tw-justify-between"><div className="tw-flex tw-justify-between tw-items-center tw-mb-3"><div className="tw-min-w-0 tw-flex-1 tw-mr-2">{submission.title && <p className="tw-mb-0 tw-text-sm tw-font-bold tw-text-iron-100 tw-truncate tw-leading-tight">{submission.title}</p>}</div>{submission.drop && <SubmissionPosition drop={submission.drop} />}</div>{submission.drop && <div className="tw-mb-3"><div className="tw-flex tw-items-center tw-gap-x-1.5 tw-flex-wrap"><div className="tw-flex tw-items-center tw-gap-x-1.5 tw-text-sm"><span className={`tw-font-medium tw-font-mono ${submission.drop.rating >= 0 ? "tw-text-iron-300" : "tw-text-iron-400"}`}>{formatNumberWithCommas(submission.drop.rating)}</span><DropVoteProgressing current={submission.drop.rating} projected={submission.drop.rating_prediction} subtle={true} /></div><div className="tw-text-sm tw-text-iron-500 tw-whitespace-nowrap"><span className="tw-font-normal">TDH total</span></div></div></div>}<div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-iron-500 tw-mt-auto"><CalendarDaysIcon className="tw-w-4 tw-h-4 tw-flex-shrink-0" /><span>{formatDate(submission.createdAt)}</span></div></div></div>{submission.drop && <SingleWaveDropVote drop={submission.drop} size={SingleWaveDropVoteSize.MINI} onVoteSuccess={handleVoteSuccess} />}</div>;
        $[14] = handleDropClick;
        $[15] = handleVoteSuccess;
        $[16] = t7;
      } else {
        t7 = $[16];
      }
      t6 = submissionsWithDrops.map(t7);
      $[10] = handleDropClick;
      $[11] = handleVoteSuccess;
      $[12] = submissionsWithDrops;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[17] !== t6) {
      t7 = <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-6">{t6}</div>;
      $[17] = t6;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    t5 = t7;
  }
  let t6;
  if ($[19] !== t4 || $[20] !== t5) {
    t6 = <div className={t4}>{t5}</div>;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  return t6;
};
function _temp(timestamp) {
  const date = new Date(timestamp);
  const currentYear = new Date().getFullYear();
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== currentYear ? "numeric" : undefined
  });
}